import * as nodeCrypto from 'crypto'

// 128-bit (16-byte) truncated HMAC-SHA256 tag — meets the >= 128-bit security requirement while keeping the
// token short enough for an email local-part. '.' separates the encoded chatId from the tag: it never appears
// in base64url or hex, and is valid inside an email local-part (reply+<token>@<inboundDomain>).
const TAG_BYTE_LENGTH = 16
const TOKEN_SEPARATOR = '.'

// `nodeCrypto` members are dereferenced lazily inside the functions, NEVER at module-eval. This server-only
// util is re-exported from the browser-universal `@ztechapps/common.utils` barrel; touching Node's `crypto`
// at eval time throws in the admin SPA (Vite externalizes `crypto`) and crashes every route. A namespace
// import only binds the module ref — property access happens when these (backend-only) functions run.
const computeTag = (chatId: string, secret: string): Buffer =>
  nodeCrypto.createHmac('sha256', secret).update(chatId).digest().subarray(0, TAG_BYTE_LENGTH)

/**
 * Builds a signed reply token that embeds a Chat conversation id and an HMAC tag.
 * The token is `base64url(chatId).hexTag` and is safe to place in an email local-part.
 */
export const buildConversationReplyToken = (chatId: string, secret: string): string => {
  const encodedChatId = Buffer.from(chatId, 'utf8').toString('base64url')
  const tag = computeTag(chatId, secret).toString('hex')
  return `${encodedChatId}${TOKEN_SEPARATOR}${tag}`
}

/**
 * Verifies a reply token and returns the embedded chatId ONLY after the HMAC tag verifies.
 * Returns null on any tamper, malformed input, or mismatch — the chatId is never trusted pre-verification.
 * Comparison uses crypto.timingSafeEqual; a length mismatch is treated as a non-match (it would otherwise throw).
 */
export const parseConversationReplyToken = (token: string, secret: string): string | null => {
  if (typeof token !== 'string') return null

  const parts = token.split(TOKEN_SEPARATOR)
  if (parts.length !== 2) return null

  const [encodedChatId, providedTagHex] = parts
  if (!encodedChatId || !providedTagHex) return null

  const chatId = Buffer.from(encodedChatId, 'base64url').toString('utf8')
  if (!chatId) return null

  const providedTag = Buffer.from(providedTagHex, 'hex')
  const expectedTag = computeTag(chatId, secret)
  if (providedTag.length !== expectedTag.length) return null
  if (!nodeCrypto.timingSafeEqual(providedTag, expectedTag)) return null

  return chatId
}
